import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  LinearProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CheckIcon from "@material-ui/icons/Check";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const KEYS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

const useStyles = makeStyles(() => ({
  page: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    fontFamily: "'Inter', 'Roboto', sans-serif",
  },
  progressBar: {
    width: "100%",
    height: 4,
    position: "fixed",
    top: 0,
    left: 0,
    zIndex: 10,
    "& .MuiLinearProgress-bar": { transition: "width 0.4s ease" },
  },
  card: {
    width: "100%",
    maxWidth: 640,
    padding: "72px 40px 40px",
    margin: "0 auto",
    flex: 1,
    "@media (max-width:600px)": { padding: "64px 20px 32px" },
  },
  logo: {
    maxHeight: 52,
    maxWidth: 180,
    objectFit: "contain",
    marginBottom: 36,
    display: "block",
  },
  stepNum: {
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginBottom: 8,
    opacity: 0.5,
  },
  question: {
    fontSize: "1.45rem",
    fontWeight: 700,
    lineHeight: 1.35,
    marginBottom: 8,
    "@media (max-width:600px)": { fontSize: "1.2rem" },
  },
  description: {
    fontSize: "0.93rem",
    lineHeight: 1.6,
    marginBottom: 24,
    opacity: 0.65,
    whiteSpace: "pre-wrap",
  },
  input: {
    width: "100%",
    marginBottom: 20,
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      fontSize: "1rem",
      backgroundColor: "rgba(0,0,0,0.04)",
    },
  },
  optionsGrid: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginBottom: 20,
  },
  optionBtn: {
    width: "100%",
    padding: "13px 18px",
    borderRadius: 10,
    border: "2px solid",
    textAlign: "left",
    fontSize: "0.93rem",
    fontWeight: 500,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 12,
    transition: "all 0.12s",
    background: "transparent",
    outline: "none",
  },
  optionKey: {
    width: 24,
    height: 24,
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.7rem",
    fontWeight: 700,
    flexShrink: 0,
    border: "1.5px solid currentColor",
  },
  scaleRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 20,
  },
  scaleBtn: {
    width: 46,
    height: 46,
    borderRadius: 10,
    border: "2px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.9rem",
    fontWeight: 600,
    cursor: "pointer",
    background: "transparent",
    outline: "none",
    transition: "all 0.12s",
  },
  yesNoRow: {
    display: "flex",
    gap: 14,
    marginBottom: 20,
    "@media (max-width:400px)": { flexDirection: "column" },
  },
  yesNoBtn: {
    flex: 1,
    padding: "15px",
    borderRadius: 12,
    border: "2px solid",
    fontSize: "1rem",
    fontWeight: 600,
    cursor: "pointer",
    background: "transparent",
    outline: "none",
    transition: "all 0.12s",
    textAlign: "center",
  },
  nextBtn: {
    padding: "13px 28px",
    borderRadius: 10,
    fontSize: "0.93rem",
    fontWeight: 600,
    cursor: "pointer",
    border: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    transition: "opacity 0.12s",
    "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
  },
  skipLink: {
    display: "block",
    marginTop: 10,
    fontSize: "0.8rem",
    cursor: "pointer",
    opacity: 0.5,
    "&:hover": { opacity: 0.75 },
  },
  completionBox: {
    textAlign: "center",
    padding: "80px 24px",
    maxWidth: 560,
    margin: "0 auto",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  checkCircle: {
    width: 72,
    height: 72,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 24px",
    "& svg": { fontSize: 36, color: "#fff" },
  },
  footer: {
    padding: "14px 24px",
    textAlign: "center",
    fontSize: "0.72rem",
    opacity: 0.38,
  },
  errorBox: {
    textAlign: "center",
    padding: 64,
    opacity: 0.6,
  },
}));

function isLight(hex) {
  try {
    const c = (hex || "#ffffff").replace("#", "");
    const r = parseInt(c.slice(0, 2), 16);
    const g = parseInt(c.slice(2, 4), 16);
    const b = parseInt(c.slice(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 155;
  } catch { return true; }
}

function resolveNextIndex(step, answer, steps) {
  const logic = step.logic;
  if (logic && Array.isArray(logic.conditions)) {
    for (const cond of logic.conditions) {
      const val = String(answer ?? "").toLowerCase();
      const condVal = String(cond.value ?? "").toLowerCase();
      let match = false;
      if (cond.operator === "equals") match = val === condVal;
      else if (cond.operator === "not_equals") match = val !== condVal;
      else if (cond.operator === "contains") match = val.includes(condVal);
      if (match && cond.nextStepId) {
        const idx = steps.findIndex(s => s.id === cond.nextStepId);
        if (idx >= 0) return idx;
      }
    }
    if (logic.defaultNextStepId) {
      const idx = steps.findIndex(s => s.id === logic.defaultNextStepId);
      if (idx >= 0) return idx;
    }
  }
  return steps.findIndex(s => s.id === step.id) + 1;
}

export default function QuizPublic({ match }) {
  const { slug } = match.params;
  const classes = useStyles();

  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [current, setCurrent] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");

  useEffect(() => {
    axios.get(`${BACKEND}/q/${slug}`)
      .then(({ data }) => {
        setQuiz(data);
        if (data.name) document.title = data.name;
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const step = quiz?.steps?.[stepIndex];
  const totalSteps = quiz?.steps?.length || 1;
  const primaryColor = quiz?.primaryColor || "#6C63FF";
  const bgColor = quiz?.bgColor || "#ffffff";
  const textColor = isLight(bgColor) ? "#1a1a1a" : "#f0f0f0";
  const mutedColor = isLight(bgColor) ? "rgba(0,0,0,0.45)" : "rgba(255,255,255,0.45)";

  useEffect(() => {
    if (step) setCurrent(answers[step.id] ?? "");
  }, [stepIndex]); // eslint-disable-line

  const curAnswer = answers[step?.id] ?? current;
  const isAnswered = (() => {
    if (!step) return false;
    if (!step.required) return true;
    const val = answers[step.id] ?? current;
    if (Array.isArray(val)) return val.length > 0;
    return String(val).trim() !== "";
  })();

  const advance = useCallback(async (overrideVal) => {
    const val = overrideVal !== undefined ? overrideVal : current;
    const newAnswers = { ...answers, [step.id]: val };
    setAnswers(newAnswers);
    setApiError("");

    if (step.type === "redirect") {
      window.location.href = step.config?.url || quiz.redirectUrl || "/";
      return;
    }

    const nextIdx = resolveNextIndex(step, val, quiz.steps);

    if (nextIdx >= quiz.steps.length) {
      setSubmitting(true);
      try {
        await axios.post(`${BACKEND}/q/${slug}/submit`, { answers: newAnswers });
        setSubmitted(true);
        if (quiz.redirectUrl) setTimeout(() => { window.location.href = quiz.redirectUrl; }, 2500);
      } catch {
        setApiError("Erro ao enviar. Tente novamente.");
      } finally {
        setSubmitting(false);
      }
    } else {
      setStepIndex(nextIdx);
    }
  }, [current, answers, step, quiz, slug]);

  useEffect(() => {
    const onKey = (e) => {
      if (!step || ["text_long", "multiple_choice"].includes(step.type)) return;
      if (e.key === "Enter" && isAnswered) advance();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isAnswered, advance, step]);

  if (loading) return (
    <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh"
      style={{ backgroundColor: bgColor }}>
      <CircularProgress style={{ color: primaryColor }} />
    </Box>
  );

  if (notFound || !quiz) return (
    <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh">
      <Box className={classes.errorBox}>
        <Typography variant="h5" gutterBottom>Formulário não encontrado</Typography>
        <Typography variant="body2">O link pode estar incorreto ou estar inativo.</Typography>
      </Box>
    </Box>
  );

  if (!quiz.steps || quiz.steps.length === 0) return (
    <Box className={classes.page} style={{ backgroundColor: bgColor, color: textColor }}>
      <Box display="flex" alignItems="center" justifyContent="center" flex={1} minHeight="80vh">
        <Box className={classes.errorBox}>
          {quiz.logoUrl && (
            <img src={quiz.logoUrl.startsWith("http") ? quiz.logoUrl : `${BACKEND}${quiz.logoUrl}`}
              alt="Logo" className={classes.logo} style={{ margin: "0 auto 24px" }} />
          )}
          <Typography variant="h5" gutterBottom style={{ fontWeight: 700 }}>
            {quiz.name}
          </Typography>
          <Typography variant="body2">Este formulário ainda não possui perguntas.</Typography>
        </Box>
      </Box>
      <Box className={classes.footer} style={{ color: mutedColor }}>
        Criado com {quiz.appName || "Floow Chat"}
      </Box>
    </Box>
  );

  if (submitted) return (
    <Box className={classes.page} style={{ backgroundColor: bgColor, color: textColor }}>
      <Box className={classes.completionBox}>
        {quiz.logoUrl && (
          <img src={quiz.logoUrl.startsWith("http") ? quiz.logoUrl : `${BACKEND}${quiz.logoUrl}`}
            alt="Logo" className={classes.logo} style={{ margin: "0 auto 32px" }} />
        )}
        <Box className={classes.checkCircle} style={{ backgroundColor: primaryColor }}>
          <CheckIcon />
        </Box>
        <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 10 }}>
          {quiz.completionMessage || "Obrigado pelas respostas!"}
        </Typography>
        {quiz.redirectUrl && (
          <Typography variant="body2" style={{ opacity: 0.5, marginTop: 8 }}>
            Redirecionando em instantes…
          </Typography>
        )}
      </Box>
      <Box className={classes.footer} style={{ color: mutedColor }}>
        Criado com {quiz.appName || "Floow Chat"}
      </Box>
    </Box>
  );

  const renderStep = () => {
    if (!step) return null;

    const imgUrl = step.config?.imageUrl;
    const resolvedImg = imgUrl
      ? (imgUrl.startsWith("http") ? imgUrl : `${BACKEND}${imgUrl}`)
      : null;

    switch (step.type) {

      case "statement":
        return (
          <>
            {resolvedImg && <img src={resolvedImg} alt="" style={{ width: "100%", borderRadius: 10, marginBottom: 20, maxHeight: 300, objectFit: "cover" }} />}
            <button className={classes.nextBtn} style={{ backgroundColor: primaryColor, color: "#fff" }}
              onClick={() => advance("")}>
              {quiz.buttonText || "Continuar"} <ChevronRightIcon style={{ fontSize: 18 }} />
            </button>
          </>
        );

      case "single_choice": {
        const opts = step.options || [];
        const sel = answers[step.id];
        return (
          <Box className={classes.optionsGrid}>
            {opts.map((opt, i) => {
              const isSel = sel === opt.value;
              return (
                <button key={i} className={classes.optionBtn}
                  style={{ borderColor: isSel ? primaryColor : `${textColor}20`, color: textColor, backgroundColor: isSel ? `${primaryColor}18` : "transparent" }}
                  onClick={() => { setAnswers(p => ({ ...p, [step.id]: opt.value })); setTimeout(() => advance(opt.value), 160); }}>
                  <span className={classes.optionKey}
                    style={{ borderColor: isSel ? primaryColor : `${textColor}40`, color: isSel ? "#fff" : mutedColor, backgroundColor: isSel ? primaryColor : "transparent" }}>
                    {isSel ? <CheckIcon style={{ fontSize: 13 }} /> : (KEYS[i] || i + 1)}
                  </span>
                  {opt.label}
                </button>
              );
            })}
          </Box>
        );
      }

      case "multiple_choice": {
        const opts = step.options || [];
        const sel = Array.isArray(answers[step.id]) ? answers[step.id] : [];
        return (
          <>
            <Box className={classes.optionsGrid}>
              {opts.map((opt, i) => {
                const isSel = sel.includes(opt.value);
                return (
                  <button key={i} className={classes.optionBtn}
                    style={{ borderColor: isSel ? primaryColor : `${textColor}20`, color: textColor, backgroundColor: isSel ? `${primaryColor}18` : "transparent" }}
                    onClick={() => {
                      const next = isSel ? sel.filter(v => v !== opt.value) : [...sel, opt.value];
                      setAnswers(p => ({ ...p, [step.id]: next }));
                      setCurrent(next);
                    }}>
                    <span className={classes.optionKey}
                      style={{ borderColor: isSel ? primaryColor : `${textColor}40`, color: isSel ? "#fff" : mutedColor, backgroundColor: isSel ? primaryColor : "transparent" }}>
                      {isSel ? <CheckIcon style={{ fontSize: 13 }} /> : (KEYS[i] || i + 1)}
                    </span>
                    {opt.label}
                  </button>
                );
              })}
            </Box>
            <button className={classes.nextBtn} style={{ backgroundColor: primaryColor, color: "#fff" }}
              disabled={!isAnswered || submitting} onClick={() => advance(sel)}>
              OK <ChevronRightIcon style={{ fontSize: 18 }} />
            </button>
          </>
        );
      }

      case "yes_no": {
        const sel = answers[step.id];
        return (
          <Box className={classes.yesNoRow}>
            {[{ label: "Sim", value: "sim" }, { label: "Não", value: "nao" }].map(({ label, value }) => {
              const isSel = sel === value;
              return (
                <button key={value} className={classes.yesNoBtn}
                  style={{ borderColor: isSel ? primaryColor : `${textColor}20`, color: isSel ? primaryColor : textColor, backgroundColor: isSel ? `${primaryColor}18` : "transparent" }}
                  onClick={() => { setAnswers(p => ({ ...p, [step.id]: value })); setTimeout(() => advance(value), 160); }}>
                  {label}
                </button>
              );
            })}
          </Box>
        );
      }

      case "scale":
      case "nps": {
        const min = step.type === "nps" ? 0 : (step.config?.min || 1);
        const max = step.type === "nps" ? 10 : (step.config?.max || 5);
        const sel = answers[step.id];
        const nums = Array.from({ length: max - min + 1 }, (_, i) => min + i);
        return (
          <>
            <Box className={classes.scaleRow}>
              {nums.map(n => {
                const isSel = String(sel) === String(n);
                return (
                  <button key={n} className={classes.scaleBtn}
                    style={{ borderColor: isSel ? primaryColor : `${textColor}20`, color: isSel ? "#fff" : textColor, backgroundColor: isSel ? primaryColor : "transparent" }}
                    onClick={() => { setAnswers(p => ({ ...p, [step.id]: n })); setCurrent(n); }}>
                    {n}
                  </button>
                );
              })}
            </Box>
            {sel !== undefined && sel !== "" && (
              <button className={classes.nextBtn} style={{ backgroundColor: primaryColor, color: "#fff" }}
                onClick={() => advance(sel)} disabled={submitting}>
                OK <ChevronRightIcon style={{ fontSize: 18 }} />
              </button>
            )}
          </>
        );
      }

      case "text_long":
        return (
          <>
            <TextField className={classes.input} variant="outlined" multiline minRows={4}
              placeholder={step.placeholder || "Digite sua resposta..."}
              value={current}
              onChange={e => { const v = e.target.value; setCurrent(v); }}
              InputProps={{ style: { color: textColor } }} autoFocus />
            <button className={classes.nextBtn} style={{ backgroundColor: primaryColor, color: "#fff" }}
              disabled={!isAnswered || submitting} onClick={() => advance()}>
              {submitting ? <CircularProgress size={16} style={{ color: "#fff" }} /> : <><span>OK</span><ChevronRightIcon style={{ fontSize: 18 }} /></>}
            </button>
          </>
        );

      case "lead_email":
        return (
          <>
            <TextField className={classes.input} variant="outlined" type="email"
              placeholder={step.placeholder || "seu@email.com"}
              value={current} onChange={e => { const v = e.target.value; setCurrent(v); }}
              InputProps={{ style: { color: textColor } }} autoFocus />
            <button className={classes.nextBtn} style={{ backgroundColor: primaryColor, color: "#fff" }}
              disabled={!isAnswered || submitting} onClick={() => advance()}>
              OK <ChevronRightIcon style={{ fontSize: 18 }} />
            </button>
          </>
        );

      case "lead_phone":
        return (
          <>
            <TextField className={classes.input} variant="outlined" type="tel"
              placeholder={step.placeholder || "(11) 99999-9999"}
              value={current} onChange={e => { const v = e.target.value; setCurrent(v); }}
              InputProps={{ style: { color: textColor } }} autoFocus />
            <button className={classes.nextBtn} style={{ backgroundColor: primaryColor, color: "#fff" }}
              disabled={!isAnswered || submitting} onClick={() => advance()}>
              OK <ChevronRightIcon style={{ fontSize: 18 }} />
            </button>
          </>
        );

      case "number":
        return (
          <>
            <TextField className={classes.input} variant="outlined" type="number"
              placeholder={step.placeholder || "0"}
              value={current} onChange={e => { const v = e.target.value; setCurrent(v); }}
              InputProps={{ style: { color: textColor } }} autoFocus />
            <button className={classes.nextBtn} style={{ backgroundColor: primaryColor, color: "#fff" }}
              disabled={!isAnswered || submitting} onClick={() => advance()}>
              OK <ChevronRightIcon style={{ fontSize: 18 }} />
            </button>
          </>
        );

      case "date":
        return (
          <>
            <TextField className={classes.input} variant="outlined" type="date"
              value={current} onChange={e => { const v = e.target.value; setCurrent(v); }}
              InputLabelProps={{ shrink: true }} InputProps={{ style: { color: textColor } }} />
            <button className={classes.nextBtn} style={{ backgroundColor: primaryColor, color: "#fff" }}
              disabled={!isAnswered || submitting} onClick={() => advance()}>
              OK <ChevronRightIcon style={{ fontSize: 18 }} />
            </button>
          </>
        );

      default: // lead_name, text_short
        return (
          <>
            <TextField className={classes.input} variant="outlined"
              placeholder={step.placeholder || "Digite aqui..."}
              value={current} onChange={e => { const v = e.target.value; setCurrent(v); }}
              InputProps={{ style: { color: textColor } }} autoFocus />
            <button className={classes.nextBtn} style={{ backgroundColor: primaryColor, color: "#fff" }}
              disabled={!isAnswered || submitting} onClick={() => advance()}>
              {submitting ? <CircularProgress size={16} style={{ color: "#fff" }} /> : <><span>OK</span><ChevronRightIcon style={{ fontSize: 18 }} /></>}
            </button>
          </>
        );
    }
  };

  const showSkip = step && !step.required && !["statement", "single_choice", "yes_no"].includes(step.type);

  return (
    <Box className={classes.page} style={{ backgroundColor: bgColor, color: textColor }}>
      {/* Barra de progresso */}
      <Box className={classes.progressBar}>
        <LinearProgress variant="determinate" value={Math.round((stepIndex / totalSteps) * 100)}
          style={{ height: 4, backgroundColor: `${primaryColor}22` }}
          classes={{ bar: "qpb" }} />
        <style>{`.qpb { background-color: ${primaryColor} !important; }`}</style>
      </Box>

      <Box className={classes.card}>
        {/* Logo apenas no primeiro step */}
        {stepIndex === 0 && quiz.logoUrl && (
          <img src={quiz.logoUrl.startsWith("http") ? quiz.logoUrl : `${BACKEND}${quiz.logoUrl}`}
            alt="Logo" className={classes.logo} />
        )}

        {step && (
          <>
            <Typography className={classes.stepNum} style={{ color: primaryColor }}>
              {stepIndex + 1} / {totalSteps}
            </Typography>

            <Typography className={classes.question} style={{ color: textColor }}>
              {step.question}
              {step.required && <span style={{ color: primaryColor }}> *</span>}
            </Typography>

            {step.description && (
              <Typography className={classes.description} style={{ color: mutedColor }}>
                {step.description}
              </Typography>
            )}

            {renderStep()}

            {apiError && (
              <Typography variant="body2" style={{ color: "#e53935", marginTop: 10 }}>
                {apiError}
              </Typography>
            )}

            {showSkip && (
              <span className={classes.skipLink} style={{ color: mutedColor }} onClick={() => advance("")}>
                Pular →
              </span>
            )}
          </>
        )}
      </Box>

      <Box className={classes.footer} style={{ color: mutedColor }}>
        Criado com {quiz.appName || "Floow Chat"}
      </Box>
    </Box>
  );
}
